import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { RealtimeProvider } from "@/context/RealtimeContext";

import StoreLayout from "@/pages/store/StoreLayout";
import Home from "@/pages/store/Home";
import Listing from "@/pages/store/Listing";
import ProductDetail from "@/pages/store/ProductDetail";
import Checkout from "@/pages/store/Checkout";
import MyOrders from "@/pages/store/MyOrders";
import OrderTracking from "@/pages/store/OrderTracking";
import Wishlist from "@/pages/store/Wishlist";
import Profile from "@/pages/store/Profile";
import Notifications from "@/pages/store/Notifications";
import Auth from "@/pages/store/Auth";
import AuthCallback from "@/pages/store/AuthCallback";
import ForgotPassword from "@/pages/store/ForgotPassword";
import ForgotEmail from "@/pages/store/ForgotEmail";
import ResetPassword from "@/pages/store/ResetPassword";
import BecomeSeller from "@/pages/store/BecomeSeller";
import SellerProducts from "@/pages/store/SellerProducts";
import SellerOrders from "@/pages/store/SellerOrders";
import SellerLayout from "@/pages/seller/SellerLayout";
import SellerDashboard from "@/pages/seller/SellerDashboard";
import SellerWallet from "@/pages/seller/SellerWallet";
import SellerPOS from "@/pages/seller/SellerPOS";
import BookService from "@/pages/store/BookService";
import LocalSellers from "@/pages/store/LocalSellers";
import AdminServiceJobs from "@/pages/admin/AdminServiceJobs";
import SalesmanSales from "@/pages/admin/SalesmanSales";
import { PaymentSuccess, PaymentCancel } from "@/pages/store/Payment";
import StaticPage from "@/pages/store/StaticPage";
import DownloadApp from "@/pages/store/DownloadApp";
import RsMartPos from "@/pages/store/RsMartPos";
import Downloads from "@/pages/store/Downloads";
import PointsHistory from "@/pages/store/PointsHistory";
import BatchChecklistPrint from "@/pages/admin/BatchChecklistPrint";
import InvoicePrint from "@/pages/admin/InvoicePrint";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSync from "@/pages/admin/AdminSync";
import AdminApiAnalytics from "@/pages/admin/AdminApiAnalytics";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminReorder from "@/pages/admin/AdminReorder";
import AdminBatches from "@/pages/admin/AdminBatches";
import AdminLoyalty from "@/pages/admin/AdminLoyalty";
import AdminProfit from "@/pages/admin/AdminProfit";
import AdminStores from "@/pages/admin/AdminStores";
import AdminWarehouses from "@/pages/admin/AdminWarehouses";
import AdminTransfers from "@/pages/admin/AdminTransfers";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminPurchaseReturns from "@/pages/admin/AdminPurchaseReturns";
import AdminSellers from "@/pages/admin/AdminSellers";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminPOS from "@/pages/admin/AdminPOS";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminBrands from "@/pages/admin/AdminBrands";
import AdminDelivery from "@/pages/admin/AdminDelivery";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminBanners from "@/pages/admin/AdminBanners";
import AdminBusinesses from "@/pages/admin/AdminBusinesses";
import AdminAgents from "@/pages/admin/AdminAgents";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminProductDefaults from "@/pages/admin/AdminProductDefaults";
import AgentPortal from "@/pages/agent/AgentPortal";
import { BusinessProvider } from "@/context/BusinessContext";
import AdminWarranties from "@/pages/admin/AdminWarranties";
import AdminAMC from "@/pages/admin/AdminAMC";
import AdminQuotations from "@/pages/admin/AdminQuotations";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminReturns from "@/pages/admin/AdminReturns";
import AdminReports from "@/pages/admin/AdminReports";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminBusiness from "@/pages/admin/AdminBusiness";
import AdminKhata from "@/pages/admin/AdminKhata";
import AdminCashbook from "@/pages/admin/AdminCashbook";
import AdminLabels from "@/pages/admin/AdminLabels";
import PublicInvoice from "@/pages/store/PublicInvoice";
import PurchaseVoucher from "@/pages/admin/PurchaseVoucher";
import PaymentVoucher from "@/pages/admin/PaymentVoucher";
import CashbookPrint from "@/pages/admin/CashbookPrint";

function Router() {
  const location = useLocation();
  // Emergent Google auth callback — detect session_id in URL fragment during render
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Auth />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/forgot-email" element={<ForgotEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/print/expiry" element={<BatchChecklistPrint />} />
      <Route path="/print/invoice/:oid" element={<InvoicePrint />} />
      <Route path="/invoice/:oid" element={<PublicInvoice />} />
      <Route path="/agent" element={<AgentPortal />} />
      <Route path="/print/purchase/:pid" element={<PurchaseVoucher />} />
      <Route path="/print/supplier-payment/:spayid" element={<PaymentVoucher />} />
      <Route path="/print/cashbook" element={<CashbookPrint />} />

      <Route path="/erp" element={<Navigate to="/admin" replace />} />
      <Route path="/admin" element={<BusinessProvider><AdminLayout /></BusinessProvider>}>
        <Route index element={<AdminDashboard />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="sync" element={<AdminSync />} />
        <Route path="api-analytics" element={<AdminApiAnalytics />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="categories" element={<AdminCategories />} />
        <Route path="inventory" element={<AdminInventory />} />
        <Route path="reorder" element={<AdminReorder />} />
        <Route path="batches" element={<AdminBatches />} />
        <Route path="profit" element={<AdminProfit />} />
        <Route path="loyalty" element={<AdminLoyalty />} />
        <Route path="stores" element={<AdminStores />} />
        <Route path="warehouses" element={<AdminWarehouses />} />
        <Route path="transfers" element={<AdminTransfers />} />
        <Route path="suppliers" element={<AdminSuppliers />} />
        <Route path="purchase-returns" element={<AdminPurchaseReturns />} />
        <Route path="sellers" element={<AdminSellers />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="service-jobs" element={<AdminServiceJobs />} />
        <Route path="my-sales" element={<SalesmanSales />} />
        <Route path="pos" element={<AdminPOS />} />
        <Route path="khata" element={<AdminKhata />} />
        <Route path="cashbook" element={<AdminCashbook />} />
        <Route path="business" element={<AdminBusiness />} />
        <Route path="labels" element={<AdminLabels />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="team" element={<AdminTeam />} />
        <Route path="coupons" element={<AdminCoupons />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="brands" element={<AdminBrands />} />
        <Route path="delivery" element={<AdminDelivery />} />
        <Route path="media" element={<AdminMedia />} />
        <Route path="banners" element={<AdminBanners />} />
        <Route path="businesses" element={<AdminBusinesses />} />
        <Route path="agents" element={<AdminAgents />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="product-defaults" element={<AdminProductDefaults />} />
        <Route path="warranties" element={<AdminWarranties />} />
        <Route path="amc" element={<AdminAMC />} />
        <Route path="quotations" element={<AdminQuotations />} />
        <Route path="bookings" element={<AdminBookings />} />
        <Route path="returns" element={<AdminReturns />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="audit" element={<AdminAudit />} />
      </Route>

      <Route path="/seller" element={<SellerLayout />}>
        <Route index element={<SellerDashboard />} />
        <Route path="products" element={<SellerProducts />} />
        <Route path="orders" element={<SellerOrders />} />
        <Route path="pos" element={<SellerPOS />} />
        <Route path="wallet" element={<SellerWallet />} />
      </Route>

      <Route path="/" element={<StoreLayout />}>
        <Route index element={<Home />} />
        <Route path="search" element={<Listing />} />
        <Route path="category/:categoryId" element={<Listing />} />
        <Route path="product/:id" element={<ProductDetail />} />
        <Route path="checkout" element={<Checkout />} />
        <Route path="orders" element={<MyOrders />} />
        <Route path="orders/:id" element={<OrderTracking />} />
        <Route path="wishlist" element={<Wishlist />} />
        <Route path="become-seller" element={<BecomeSeller />} />
        <Route path="services" element={<BookService />} />
        <Route path="local-sellers" element={<LocalSellers />} />
        <Route path="profile" element={<Profile />} />
        <Route path="rewards" element={<PointsHistory />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="page/:slug" element={<StaticPage />} />
        <Route path="download-app" element={<DownloadApp />} />
        <Route path="rs-mart-pos" element={<RsMartPos />} />
        <Route path="downloads" element={<Downloads />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <RealtimeProvider>
        <CartProvider>
          <BrowserRouter>
            <Router />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </CartProvider>
      </RealtimeProvider>
    </AuthProvider>
  );
}
